import { type Cell } from "~/runtime/cell-compiler";

import { type Cell as CellApi } from "../../../../models/cell";
import { type FunctionBodyCell } from "../../../../utils/function-body-cell";

/* Note this interface is designed to present a facade of the internal
 * Observable variable. Observable's runtime isn't typed and so this provides
 * some helper
 */
export interface ObservableVariable {
	/* define has three call signatures depending on the number of arguments
	 *
	 * define(definition: ()=>void) => void
	 *
	 * define(inputs: string[] | string, definition: () => void) => void
	 *
	 * define(name: string, inputs: string[], definition: () => void) => void
	 *
	 * unfortunately this is maddeningly hard to type correctly.
	 */
	define: (name: unknown, inputs?: unknown, definition?: unknown) => void;
	delete: () => void;
	import: (imported: string, localIdentifier: string, module: ObservableModule) => void;
}

export interface ObservableModule {
	variable: (observer?: CellObserver) => ObservableVariable;
}

/**
 * Represents the result of a cell's computation. These fields are *mutually exclusive*, i.e., a
 * cell is either pending, or it has a value, or it has a static error, or it has a runtime error.
 */
export type Result = PendingResult | FulfilledResult | RejectedResult;

/**
 * Indicates the cell's computation is registered to run but has not produced a result. `staticError`
 * indicates something like a parse error occurred.
 */
export interface PendingResult {
	type: "PENDING";

	/** Error arising from static analysis, if any. For example, lexing/parsing errors. */
	staticError?: Error;

	/** True when cell has begun to run, but has not yet produced a result. False otherwise. */
	pending: boolean;
}

export const isPendingResult = (o: Result): o is PendingResult => o.type === "PENDING";

/**
 * Indicates the cell's computation has produced a result.
 *
 * If `staticError` is set, the code currently in the cell can't be run (e.g., there is a parse
 * error), but the previous version of the cell is still running, and has produced a result.
 */
export interface FulfilledResult {
	type: "FULFILLED";

	/** Error arising from static analysis, if any. For example, lexing/parsing errors. */
	staticError?: Error;

	/** The last value produced by a cell, if any. IMPORTANT NOTE: this value can be `undefined`! */
	value: unknown | undefined;
}

export const isFulfilledResult = (o?: Result): o is FulfilledResult => o?.type === "FULFILLED";

/**
 * Indicates the cell's computation has produced a runtime error.
 *
 * If `staticError` is set, the code currently in the cell can't be run (e.g., there is a parse
 * error), but the previous version of the cell is still running, and has produced a runtime error.
 */
export interface RejectedResult {
	type: "REJECTED";

	/** Error arising from static analysis, if any. For example, lexing/parsing errors. */
	staticError?: Error;

	/**
	 * Error produced by the running cell, if any. For example, an exception thrown from the cell's
	 * code.
	 */
	runtimeError: Error;
}

export const isRejectedResult = (o?: Result): o is RejectedResult => o?.type === "REJECTED";

/**
 * Interface implemented by anything that needs to "observes" updates produced by a cell.
 *
 * For example, if we pass observer that prints all results produced by a cell to the runtime, it
 * will call `fulfilled(value)` with the current `value` any time the cell produces a value.
 */
export interface CellObserver {
	/**
	 * Called by the runtime any time a cell produces an error during initialization, e.g., an
	 * import fails or a cell fails to parse.
	 */
	initError: (staticError: Error) => void;

	/** Called by the runtime when a cell is running but has not produced a result. */
	pending: () => void;

	/** Called by the runtime any time a cell produces a value. */
	fulfilled: (value: unknown) => void;

	/**
	 * Called by the runtime any time a cell produces a runtime error, e.g., throws an exception at
	 * runtime.
	 */
	rejected: (runtimeError: Error) => void;
}

export interface ResultsObserver {
	next: (result: Result) => void;
}

/**
 * Represents an object which needs to be cleaned up before being destroyed, where cleanup is
 * performed by calling the `dispose` method.
 */
export interface Disposable {
	dispose: () => void;
}

export interface JSON {
	type: "json";
	json: unknown;
}

export interface Markdown {
	type: "markdown";
	body: string;
}

export type Parsed = Cell | FunctionBodyCell | JSON | Markdown | undefined | null;

/**
 * The handle for a cell executed and cached by the runtime.
 */
export interface CachedCellHandle extends Disposable {
	cellSpec: CellApi;
	setSubscriber: (observer: ResultsObserver) => void;
}
